
import React, { useState, useRef } from 'react';
import { storage } from '../services/storage';
import { Camera, Image as ImageIcon, Loader2, Sparkles, AlertCircle, X, ChevronRight, Sprout, Link2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { t } from '../services/i18n';
import { fileToBase64Compressed } from '../services/imageUtils';

export const PlantAnalysis: React.FC = () => {
  const [image, setImage] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<{ text: string; sources?: { uri: string; title: string }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAnalyzing(true);
      try {
        const compressedBase64 = await fileToBase64Compressed(e.target.files[0]);
        setImage(compressedBase64);
        setResult(null);
        setError(null);
      } catch (err) {
        console.error("Erro ao processar imagem:", err);
        setError("Erro ao processar imagem.");
      } finally {
        setAnalyzing(false);
      }
    }
  };

  const startAnalysis = async () => {
    if (!image) return;
    setAnalyzing(true);
    setError(null);
    try {
      const analysis = await storage.identifyPlantAndHealth(image);
      setResult(analysis);
    } catch (err) {
      setError(t('analysis_error'));
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    setImage(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in pb-12">
      <div className="text-center mb-2">
        <h2 className="text-2xl font-black text-white flex items-center justify-center gap-2">
          <Sparkles className="text-raizama-action" size={24} />
          {t('analysis_title')}
        </h2>
        <p className="text-xs text-raizama-textSec uppercase tracking-widest font-bold mt-1">{t('analysis_subtitle')}</p>
      </div>

      {!image ? (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="aspect-[4/3] bg-raizama-surface border-2 border-dashed border-raizama-border/30 rounded-3xl flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-raizama-action hover:bg-raizama-action/5 transition-all shadow-xl group"
        >
          <div className="w-16 h-16 rounded-full bg-raizama-action/10 flex items-center justify-center text-raizama-action group-hover:scale-110 transition-transform">
            <Camera size={32} />
          </div>
          <div className="text-center">
            <p className="text-white font-bold">{t('analysis_take_photo')}</p>
            <p className="text-xs text-raizama-textSec">{t('analysis_gallery')}</p>
          </div>
          <input 
            type="file" 
            accept="image/*" 
            capture="environment" 
            ref={fileInputRef} 
            className="hidden" 
            onChange={handleImageUpload} 
          />
        </div>
      ) : (
        <div className="space-y-6 animate-scale-up">
          <div className="relative rounded-3xl overflow-hidden border border-raizama-border/20 shadow-2xl h-64">
            <img src={image} className="w-full h-full object-cover" alt="Preview" />
            {!analyzing && !result && (
              <button 
                onClick={reset}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-red-500 transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>

          {!result && (
            <button 
              onClick={startAnalysis}
              disabled={analyzing}
              className="w-full bg-raizama-action hover:bg-raizama-highlight text-white font-black py-4 rounded-2xl shadow-lg shadow-raizama-action/30 flex items-center justify-center gap-3 transition-all active:scale-95 disabled:opacity-50"
            >
              {analyzing ? (
                <>
                  <Loader2 className="animate-spin" size={24} />
                  <span>{t('analysis_analyzing_btn')}</span>
                </>
              ) : (
                <>
                  <Sparkles size={24} />
                  <span>{t('analysis_start_btn')}</span>
                </>
              )}
            </button>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-2xl flex items-center gap-3 text-red-400 text-sm animate-pulse">
              <AlertCircle size={20} />
              <p>{error}</p>
            </div>
          )}

          {result && (
            <div className="space-y-4 animate-fade-in">
              <div className="bg-raizama-surface rounded-3xl p-6 border border-raizama-action/30 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-raizama-action/10 flex items-center justify-center text-raizama-action">
                    <Sprout size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-black text-sm uppercase tracking-wider">{t('analysis_result_title')}</h3>
                    <p className="text-[10px] text-raizama-textSec uppercase font-bold">{t('analysis_result_subtitle')}</p>
                  </div>
                </div>

                <div className="prose prose-invert prose-sm max-w-none text-raizama-textSec leading-relaxed">
                  <div className="whitespace-pre-wrap mb-6">
                    {result.text}
                  </div>
                </div>

                {/* Exibição das Fontes de Pesquisa (Grounding) */}
                {result.sources && result.sources.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-raizama-border/10">
                    <h4 className="text-[9px] font-black text-raizama-textSec uppercase tracking-[0.2em] mb-3 flex items-center gap-1.5">
                      <Link2 size={10} /> {t('home_ai_sources')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {result.sources.map((source, idx) => (
                        <a 
                          key={idx} 
                          href={source.uri} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 bg-black/20 hover:bg-raizama-action/10 px-2.5 py-1 rounded-md border border-raizama-border/5 text-[9px] text-raizama-textSec hover:text-raizama-action transition-all truncate max-w-[140px]"
                        >
                          <ExternalLink size={10} />
                          {source.title || t('analysis_reference')}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={reset}
                  className="py-4 bg-raizama-surface border border-raizama-border/20 text-white font-bold rounded-2xl hover:bg-white/5 transition-colors"
                >
                  {t('analysis_new_btn')}
                </button>
                <button 
                  onClick={() => navigate('/add', { state: { scanResult: result.text, scanImage: image } })}
                  className="py-4 bg-raizama-action text-white font-bold rounded-2xl shadow-lg hover:brightness-110 flex items-center justify-center gap-2"
                >
                  {t('analysis_catalog_btn')} <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {analyzing && (
        <div className="bg-raizama-surface/50 p-6 rounded-3xl border border-dashed border-raizama-border/20 text-center animate-pulse">
          <p className="text-xs text-raizama-highlight font-black uppercase tracking-widest mb-2">{t('analysis_processing_title')}</p>
          <p className="text-xs text-raizama-textSec">{t('analysis_processing_desc')}</p>
        </div>
      )}
    </div>
  );
};
